import XMarkIcon from '@heroicons/react/20/solid/XMarkIcon.js';
import React from 'react';
import { useIsHidden } from 'react-aria/private/collections/Hidden';
import {
  Button as AriaButton,
  Tag as AriaTag,
  TagGroup as AriaTagGroup,
  TagList as AriaTagList,
} from 'react-aria-components';

import { tv } from '../../tv';
import { initialsOf } from '../avatar/initials';
import { autoColor, type AutoLabelColor } from '../tag/auto-color';
import { tagStyles } from '../tag/tag';
import type { OptionMeta } from './option';

/** One chosen option as a multiple picker shows it. */
export interface ChipEntry {
  id: string;
  label: string;
  meta?: OptionMeta;
}

const chipStyles = tv({
  slots: {
    list: 'ck:flex ck:min-w-0 ck:flex-wrap ck:items-center ck:gap-1',
    chip: 'ck:cursor-default ck:outline-none ck:focus-visible:focus-ring',
    /* A person's chip: a pill, its disc at the start. */
    person: 'ck:rounded-full ck:ps-0 ck:gap-1',
    /* The single line's hidden twin: every chip at its natural width, out of
     * sight and out of the accessibility tree, so the row can be fitted. */
    measure:
      'ck:invisible ck:absolute ck:top-0 ck:left-0 ck:flex ck:flex-nowrap ck:gap-1 ck:pointer-events-none',
  },
  variants: {
    /* One row whatever is chosen: the chips that fit, then a count of the
     * rest. Only the first chip may shrink, its label truncating, so one
     * long name still leaves room for the count. */
    singleLine: {
      true: {
        list: 'ck:flex-nowrap ck:overflow-hidden ck:[&>*]:shrink-0 ck:[&>*:first-child]:shrink',
      },
      false: {},
    },
    /* Inline, the grid takes no box: its chips flow in the picker's own row,
     * so a combobox's entry continues after the last chip. */
    inline: {
      true: { list: 'ck:contents' },
      false: {},
    },
  },
  defaultVariants: { inline: false, singleLine: false },
});

interface PickerChipsProps {
  /** The group's accessible name: the field's label. */
  label: string;
  /** The id of the element naming the field, when it has no label of its own. */
  labelledBy?: string;
  entries: ChipEntry[];
  size: 'md' | 'lg';
  /** Names each remove; with `onRemove`, the chips can be removed. */
  removeLabel?: (label: string) => string;
  /** Absent, the chips cannot be removed (read-only). */
  onRemove?: (ids: string[]) => void;
  /** A disabled field: the chips are shown, out of reach, as plain tags. */
  isDisabled?: boolean;
  /** The grid's id, so the picker's trigger can be described by it. */
  id?: string;
  listRef?: React.Ref<HTMLDivElement>;
  /** Keys pressed on a chip, before the grid handles them (a combobox sends them back to its entry). */
  onKeyDownCapture?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  /** The chips flow in the parent's row rather than in a box of their own. */
  isInline?: boolean;
  /** One row: the chips that fit, then a "+N" count of the others. */
  isSingleLine?: boolean;
}

/* How many chips fit the row's width with the count after them: at least
 * one, which shrinks if it has to. */
function useFittedCount(
  isSingleLine: boolean,
  entries: ChipEntry[],
  rowRef: React.RefObject<HTMLDivElement | null>,
  measureRef: React.RefObject<HTMLDivElement | null>,
) {
  const [count, setCount] = React.useState(entries.length);
  const signature = entries.map((entry) => `${entry.id}:${entry.label}`).join('|');
  React.useLayoutEffect(() => {
    if (!isSingleLine) return;
    const row = rowRef.current;
    const measure = measureRef.current;
    if (!row || !measure) return;
    /* The first ancestor with a box: the group's wrappers take none. */
    let box: HTMLElement | null = row.parentElement;
    while (box && box.getClientRects().length === 0) box = box.parentElement;
    if (!box) return;
    const fit = () => {
      const widths = [...measure.children].map((child) => (child as HTMLElement).offsetWidth);
      const more = widths.pop() ?? 0;
      const gap = parseFloat(getComputedStyle(measure).columnGap) || 0;
      const available = box.clientWidth;
      let used = 0;
      let fitted = 0;
      for (const width of widths) {
        const next = used + (fitted > 0 ? gap : 0) + width;
        const rest = widths.length - fitted - 1;
        if (next + (rest > 0 ? gap + more : 0) > available) break;
        used = next;
        fitted += 1;
      }
      setCount(Math.max(1, fitted));
    };
    fit();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(fit);
    observer.observe(box);
    // The twins size to their content, so a chip that changes width alone (a read-only field drops its remove) refits too.
    observer.observe(measure);
    return () => observer.disconnect();
  }, [isSingleLine, signature, rowRef, measureRef]);
  return isSingleLine ? Math.min(count, entries.length) : entries.length;
}

/**
 * The chosen options of a multiple picker as chips: Tag's recipe on React
 * Aria's TagGroup, so the chips are one tab stop, arrow keys move between
 * them, and Backspace or Delete on a chip removes it. A label option is a
 * Tag in its color; a person is an avatar chip. Internal to the pickers.
 */
export function PickerChips({
  label,
  labelledBy,
  entries,
  size,
  removeLabel,
  onRemove,
  isDisabled = false,
  id,
  listRef,
  onKeyDownCapture,
  isInline = false,
  isSingleLine = false,
}: PickerChipsProps) {
  const isHidden = useIsHidden();
  const { list, chip, person, measure } = chipStyles({
    inline: isInline,
    singleLine: isSingleLine,
  });
  const rowRef = React.useRef<HTMLDivElement | null>(null);
  const measureRef = React.useRef<HTMLDivElement | null>(null);
  const setRowRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      rowRef.current = node;
      if (typeof listRef === 'function') listRef(node);
      else if (listRef) (listRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
    },
    [listRef],
  );
  const shown = useFittedCount(isSingleLine, entries, rowRef, measureRef);
  /* The picker renders whole in React Aria's hidden collection pass, where
   * a TagGroup has no list state to attach to; the chips are not options. */
  if (isHidden) return null;
  const canRemove = onRemove != null && removeLabel != null;
  const visible = entries.slice(0, shown);
  const hidden = entries.slice(shown);
  const extras = isSingleLine ? (
    <>
      {hidden.length > 0 ? (
        <MoreChip size={size} count={hidden.length} labels={hidden.map((entry) => entry.label)} />
      ) : null}
      <div ref={measureRef} aria-hidden="true" className={measure()}>
        {entries.map((entry) => (
          <ChipBody
            key={entry.id}
            entry={entry}
            size={size}
            personClass={person()}
            hasRemoveSpace={canRemove && !isDisabled}
          />
        ))}
        <MoreChip size={size} count={entries.length} labels={[]} />
      </div>
    </>
  ) : null;
  /* A grid is always a tab stop; a disabled field is skipped by Tab, so its
   * chips are plain tags with no grid around them. */
  if (isDisabled) {
    return (
      <div id={id} className="ck:contents">
        <div ref={setRowRef} className={list()} data-component="chips">
          {visible.map((entry) => (
            <ChipBody key={entry.id} entry={entry} size={size} personClass={person()} />
          ))}
        </div>
        {extras}
      </div>
    );
  }
  return (
    <div className="ck:contents" onKeyDownCapture={onKeyDownCapture}>
      <AriaTagGroup
        id={id}
        aria-label={labelledBy ? undefined : label}
        aria-labelledby={labelledBy}
        onRemove={canRemove ? (keys) => onRemove([...keys].map(String)) : undefined}
        className="ck:contents"
        data-component="chips"
      >
        <AriaTagList ref={setRowRef} items={visible} className={list()}>
          {(entry) => (
            <Chip
              entry={entry}
              size={size}
              removeLabel={canRemove ? removeLabel : undefined}
              className={chip()}
              personClass={person()}
            />
          )}
        </AriaTagList>
        {/* Inside the group, whose id describes the trigger: the hidden labels join that description. */}
        {extras}
      </AriaTagGroup>
    </div>
  );
}

/* The count of the chips the row has no room for: a gray Tag, named by the
 * labels it stands for, which the list shows as checked. */
function MoreChip({ size, count, labels }: { size: 'md' | 'lg'; count: number; labels: string[] }) {
  const { root, label } = tagStyles({ color: 'gray', size });
  return (
    <span
      className={root({ class: 'ck:shrink-0' })}
      title={labels.length > 0 ? labels.join(', ') : undefined}
      data-component="chips-more"
    >
      <span className={label()} aria-hidden={labels.length > 0 || undefined}>{`+${count}`}</span>
      {labels.length > 0 ? <span className="ck:sr-only">{labels.join(', ')}</span> : null}
    </span>
  );
}

/* A person's chip takes the person's hue, the same autoColor of their name
 * their Avatar wears everywhere else, so chip and avatar are one color. */
function personHue(entry: ChipEntry): AutoLabelColor | undefined {
  const avatar = entry.meta?.avatar;
  if (!avatar) return undefined;
  const name = (avatar.props as { name?: string }).name ?? entry.label;
  return autoColor(name);
}

function chipLook(entry: ChipEntry, size: 'md' | 'lg') {
  const color = personHue(entry) ?? entry.meta?.color;
  /* A chosen option with no label color wears the selection's tint, so the
   * chips read as what was picked rather than as gray data; a label keeps its own color. */
  return tagStyles({ color, size, selection: color == null });
}

/* The person's mark: one initial, their photo, or a team's glyph, on a disc one step
 * stronger than the chip in the same hue, inset 2px (the chip's transparent
 * border plus this margin). One initial: two letters do not fit a 16px
 * disc at the type scale's smallest step. */
const discStyles = tv({
  base: [
    'ck:ms-px ck:inline-flex ck:shrink-0 ck:items-center ck:justify-center ck:overflow-hidden',
    'ck:text-xs ck:font-semibold ck:leading-none ck:select-none',
  ],
  variants: {
    size: { md: 'ck:size-4', lg: 'ck:size-5' },
    /* The radius lives only here: a base rounded-full beside rounded-1 would
     * leave both, and the stylesheet's order would keep the circle. */
    square: { true: 'ck:rounded-1', false: 'ck:rounded-full' },
    color: {
      red: 'ck:bg-red-a-5 ck:text-red-text-soft',
      orange: 'ck:bg-orange-a-5 ck:text-orange-text-soft',
      yellow: 'ck:bg-yellow-a-5 ck:text-yellow-text-soft',
      green: 'ck:bg-green-a-5 ck:text-green-text-soft',
      teal: 'ck:bg-teal-a-5 ck:text-teal-text-soft',
      cyan: 'ck:bg-cyan-a-5 ck:text-cyan-text-soft',
      blue: 'ck:bg-blue-a-5 ck:text-blue-text-soft',
      indigo: 'ck:bg-indigo-a-5 ck:text-indigo-text-soft',
      purple: 'ck:bg-purple-a-5 ck:text-purple-text-soft',
      pink: 'ck:bg-pink-a-5 ck:text-pink-text-soft',
      brown: 'ck:bg-brown-a-5 ck:text-brown-text-soft',
    },
  },
});

function PersonDisc({
  avatar,
  hue,
  size,
}: {
  avatar: React.ReactElement;
  hue: AutoLabelColor;
  size: 'md' | 'lg';
}) {
  const {
    name = '',
    src,
    shape,
    icon: Glyph,
  } = avatar.props as {
    name?: string;
    src?: string;
    shape?: string;
    icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  };
  const [failedSrc, setFailedSrc] = React.useState<string>();
  const initial = [...initialsOf(name)][0] ?? '';
  return (
    <span
      aria-hidden="true"
      className={discStyles({ size, color: hue, square: shape === 'square' })}
      data-component="person-disc"
    >
      {Glyph ? (
        <Glyph aria-hidden="true" focusable="false" className="ck:size-3" />
      ) : src && src !== failedSrc ? (
        <img
          src={src}
          alt=""
          className="ck:size-full ck:object-cover"
          onError={() => setFailedSrc(src)}
        />
      ) : (
        initial
      )}
    </span>
  );
}

/* The disabled chip: the same look, no row, no remove. */
function ChipBody({
  entry,
  size,
  personClass,
  hasRemoveSpace = false,
}: {
  entry: ChipEntry;
  size: 'md' | 'lg';
  personClass: string;
  /** The measuring twin of a removable chip: the remove's room, without the button. */
  hasRemoveSpace?: boolean;
}) {
  const { root, icon, label, remove, removeIcon } = chipLook(entry, size);
  const Glyph = entry.meta?.icon;
  const hue = personHue(entry);
  return (
    <span
      className={root({ class: entry.meta?.avatar ? personClass : undefined })}
      data-component="chip"
      data-color={entry.meta?.color}
      data-person={entry.meta?.avatar ? true : undefined}
    >
      {hue && entry.meta?.avatar ? (
        <PersonDisc avatar={entry.meta.avatar} hue={hue} size={size} />
      ) : Glyph ? (
        <Glyph aria-hidden="true" focusable="false" className={icon()} />
      ) : null}
      <span className={label()}>{entry.label}</span>
      {hasRemoveSpace ? (
        <span className={remove()}>
          <XMarkIcon aria-hidden="true" className={removeIcon()} />
        </span>
      ) : null}
    </span>
  );
}

function Chip({
  entry,
  size,
  removeLabel,
  className,
  personClass,
}: {
  entry: ChipEntry;
  size: 'md' | 'lg';
  removeLabel?: (label: string) => string;
  className: string;
  personClass: string;
}) {
  const removeId = React.useId();
  const meta = entry.meta;
  const { root, icon, label, remove, removeIcon } = chipLook(entry, size);
  const Glyph = meta?.icon;
  const hue = personHue(entry);
  return (
    <AriaTag
      id={entry.id}
      textValue={entry.label}
      className={root({ class: [className, meta?.avatar ? personClass : undefined] })}
      data-component="chip"
      data-color={meta?.color}
      data-person={meta?.avatar ? true : undefined}
    >
      {hue && meta?.avatar ? (
        <PersonDisc avatar={meta.avatar} hue={hue} size={size} />
      ) : Glyph ? (
        <Glyph aria-hidden="true" focusable="false" className={icon()} />
      ) : null}
      <span className={label()}>{entry.label}</span>
      {removeLabel ? (
        /* Named by its own label alone: React Aria would add the chip's
         * name to it, and removeLabel already says it. Out of the tab order,
         * so the chips stay one stop (Backspace or Delete on the chip is
         * the keyboard's remove), and focus stays where it is on a press. */
        <AriaButton
          slot="remove"
          id={removeId}
          aria-label={removeLabel(entry.label)}
          aria-labelledby={removeId}
          className={remove()}
          excludeFromTabOrder
          preventFocusOnPress
        >
          <XMarkIcon aria-hidden="true" className={removeIcon()} />
        </AriaButton>
      ) : null}
    </AriaTag>
  );
}
